import { z } from "zod";
import { ParsedColumnSchema } from "./sampleUpload";

// Re-export the reused sample-upload schemas/types verbatim. The Optro pull
// flow confirms columns through the exact same backend models as the CSV/Excel
// upload (column selections / mappings / result), so the contract guardrail is
// shared. Step 2 is fed from the BE-assembled grid (E2) rather than a file
// parse, so `ParsedColumn` is the shared column shape.
export {
  ColumnSelectionSchema,
  ColumnAttributeMappingSchema,
  SampleUploadResultSchema,
} from "./sampleUpload";
export type {
  ParsedColumn,
  ColumnSelection,
  ColumnAttributeMapping,
  SampleUploadResult,
  GeneratedAttributeType,
} from "./sampleUpload";

// Endpoint 1 (E1) — list the linked control's test cycles.
//
// `GET /controls/external/control/{controlId}/tests`
// Response (camelCase): `{ tests: [{ id, name, sampleCount }] }`.
// `sampleCount === 0` cycles render non-selectable in Step 1.
export const OptroTestCycleSchema = z.object({
  id: z.number(),
  name: z.string(),
  sampleCount: z.number(),
});

export type OptroTestCycle = z.infer<typeof OptroTestCycleSchema>;

export const OptroTestCyclesResponseSchema = z.object({
  tests: z.array(OptroTestCycleSchema),
});

export type OptroTestCyclesResponse = z.infer<
  typeof OptroTestCyclesResponseSchema
>;

// Endpoint 2 (E2) — fetch a chosen cycle's grid as detected columns + preview.
//
// `GET /controls/external/control/{controlId}/tests/{testId}/grid`
// Response (camelCase, SLIM — no file fields):
//   `{ columns: ParsedColumn[], previewRows, totalRowCount, isFirstUpload }`.
// `columns` reuses `ParsedColumnSchema`, which strips the extra BE fields
// (`match_on`/`source_row_display`) so `DetectedColumnsTable`/`PreviewRowsTable`
// are reused unchanged. Empty grid cells (e.g. the header-only A/B columns)
// arrive as null and render as blanks.
export const OptroGridResponseSchema = z.object({
  columns: z.array(ParsedColumnSchema),
  previewRows: z.array(
    z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  ),
  totalRowCount: z.number(),
  isFirstUpload: z.boolean(),
});

export type OptroGridResponse = z.infer<typeof OptroGridResponseSchema>;
